import { useState } from "react";
import { LicenseTypes } from "../types/LicenseTypes";

export const useCommonUtil = () => {
  const formatPhoneNumber = (phoneNumber: string): string => {
    const cleaned = phoneNumber.replace(/\D/g, ""); // 숫자 이외의 문자 제거
    const match = cleaned.match(/^(\d{3})(\d{3,4})(\d{4})$/); // 숫자를 그룹으로 나누기
    if (match) {
      return `${match[1]}-${match[2]}-${match[3]}`; // 하이픈으로 구분하여 반환
    }
    return phoneNumber; // 변환할 수 없는 경우 원래 값 반환
  };

  function getNextDayIfPast(inputDate: Date): Date {
    const currentDate = new Date(); // 현재 날짜 가져오기

    // 입력된 날짜가 현재 날짜보다 과거인 경우
    if (inputDate < currentDate) {
      const nextDay = new Date(currentDate);
      nextDay.setDate(nextDay.getDate() + 1); // 현재 날짜에 하루를 더함

      // "yyyyMMdd" 형식으로 포맷팅
      const year = nextDay.getFullYear();
      const month = (nextDay.getMonth() + 1).toString().padStart(2, "0");
      const day = nextDay.getDate().toString().padStart(2, "0");

      return new Date(year, Number(month), Number(day));
    }

    // 과거가 아닌 경우에는 입력된 날짜를 "yyyyMMdd" 형식으로 포맷팅하여 반환
    const year = inputDate.getFullYear();
    const month = (inputDate.getMonth() + 1).toString().padStart(2, "0");
    const day = inputDate.getDate().toString().padStart(2, "0");

    return new Date(year, Number(month), Number(day));
  }

  const formatDate = (birthday: string): string => {
    if (birthday.length !== 6) {
      return birthday;
    }
    const year = birthday.slice(0, 2);
    const month = birthday.slice(2, 4);
    const day = birthday.slice(4, 6);

    const currentYear = new Date().getFullYear();
    const fullYear =
      parseInt(year) + (parseInt(year) >= currentYear % 100 ? 1900 : 2000); // 1900년과 2000년도 구분

    return `${fullYear}-${month}-${day}`;
  };

  // 날짜를 "yyyy-MM-dd HH:mm" 형식으로 변환하는 함수
  function formatDate2(dateString: string): string {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  }

  // 현재 시간과 지정된 날짜를 비교하는 함수
  function compareWithCurrentTime(targetDateString: string) {
    const targetDate = new Date(targetDateString);
    const currentDate = new Date();

    if (targetDate < currentDate) {
      return false;
    } else if (targetDate > currentDate) {
      return true;
    } else {
      return null;
    }
  }

  const calculateDrivingExperience = (
    drivingLicenseDate: string
  ): number | null => {
    // 현재 날짜를 구합니다.
    const currentDate = new Date();

    // 운전면허 발급일에서 년도, 월, 일을 추출합니다.
    const year = Number(drivingLicenseDate.slice(0, 4));
    const month = Number(drivingLicenseDate.slice(4, 6)) - 1; // 월은 0부터 시작하므로 1을 빼줍니다.
    const day = Number(drivingLicenseDate.slice(6, 8));

    // 운전면허 발급일을 Date 객체로 변환합니다.
    const licenseDate = new Date(year, month, day);

    // 운전 경력을 계산합니다.
    const experienceMilliseconds =
      currentDate.getTime() - licenseDate.getTime();
    const experienceYears =
      experienceMilliseconds / (1000 * 60 * 60 * 24 * 365.25); // 365.25일을 1년으로 간주하여 계산합니다.

    // 소수점 첫째 자리까지 반올림하여 운전 경력을 반환합니다.
    return Math.round(experienceYears * 10) / 10;
  };

  const calculateAge = (birthdate: string): number | null => {
    // 현재 날짜를 구합니다.
    const currentDate = new Date();

    // 생년월일에서 년도, 월, 일을 추출합니다.
    const year = Number(birthdate.slice(0, 2));
    const month = Number(birthdate.slice(2, 4)) - 1; // 월은 0부터 시작하므로 1을 빼줍니다.
    const day = Number(birthdate.slice(4, 6));

    // 생년월일을 Date 객체로 변환합니다. 여기서 1900년대 출생자와 2000년대 출생자를 구분합니다.
    const birthDate = new Date(
      year < 24 ? 2000 + year : 1900 + year,
      month,
      day
    );

    // 현재 날짜와 생년월일 사이의 차이를 계산합니다.
    const ageMilliseconds = currentDate.getTime() - birthDate.getTime();
    const ageYears = ageMilliseconds / (1000 * 60 * 60 * 24 * 365.25); // 365.25일을 1년으로 간주하여 계산합니다.

    // 소수점 첫째 자리까지 반올림하여 나이를 반환합니다.
    return Math.floor(ageYears);
  };

  const isValidDate = (dateString: string) => {
    // 날짜 형식을 검증하기 위한 정규 표현식
    const datePattern = /^\d{4}-\d{2}-\d{2}$/;

    // 정규 표현식과 문자열을 비교하여 검증
    if (!datePattern.test(dateString)) {
      return false;
    }

    // 유효한 날짜인지 확인
    const [year, month, day] = dateString.split("-").map(Number);

    if (month < 1 || month > 12) {
      return false; // 올바른 월 범위 확인
    }

    const maxDay = new Date(year, month, 0).getDate(); // 해당 월의 최대 일 수 계산
    if (day < 1 || day > maxDay) {
      return false; // 올바른 일 범위 확인
    }

    return true; // 모든 검증 통과
  };

  const isValidDate2 = (dateString: string) => {
    // 날짜 형식을 검증하기 위한 정규 표현식 (YYYYMMDD)
    const datePattern = /^\d{8}$/;

    // 정규 표현식과 문자열을 비교하여 검증
    if (!datePattern.test(dateString)) {
      return false;
    }

    // 유효한 날짜인지 확인
    const year = parseInt(dateString.substr(0, 4), 10);
    const month = parseInt(dateString.substr(4, 2), 10);
    const day = parseInt(dateString.substr(6, 2), 10);

    if (month < 1 || month > 12) {
      return false; // 올바른 월 범위 확인
    }

    const maxDay = new Date(year, month, 0).getDate(); // 해당 월의 최대 일 수 계산
    if (day < 1 || day > maxDay) {
      return false; // 올바른 일 범위 확인
    }

    return true; // 모든 검증 통과
  };

  const formatLicenseType = (type: string = ""): string => {
    return LicenseTypes.find(item => item.erpCode === type)?.code ?? "";
  };

  const formatLicenseType2 = (type: string = ""): string => {
    return LicenseTypes.find(item => item.code === type)?.erpCode ?? "";
  };

  const formatLicenseName = (type: string): string => {
    return LicenseTypes.find(item => item.code === type)?.description ?? "";
  };

  const formatLicenseNameText = (type: string): string => {
    return LicenseTypes.find(item => item.description === type)?.code ?? "";
  };

  const formatNumber = (value: number): string => {
    const formattedValue = value.toLocaleString("ko-KR");
    return formattedValue;
  };

  const addHyphenToLicenseNumber = (licenseNumber: string): string => {
    if (!licenseNumber) return licenseNumber;
    // 앞 두글자가 숫자가 아니면
    if (!/^\d{2}/.test(licenseNumber)) {
      // 이미 하이픈이 포함된 형태인지 확인
      const isAlreadyFormatted = /^[가-힣]{2,4}-?\d{2}-\d{6}-\d{2}$/.test(licenseNumber);
      if (isAlreadyFormatted) return licenseNumber; // 이미 형식에 맞게 하이픈이 있는 경우 그대로 반환
      
      let trimmedLicenseNumber = licenseNumber.replace(/\s/g, '');
      let removedHyphenLicenseNumber = trimmedLicenseNumber.replace(/-/g, "");
      // 하이픈을 추가하여 반환
      return removedHyphenLicenseNumber.replace(/^([가-힣]{2,4})(\d{2})(\d{6})(\d{2})/,'$1-$2-$3-$4');
    } else {
      // 이미 하이픈이 포함된 형태인지 확인
      const isAlreadyFormatted = /^[0-9]{2}-[0-9]{2}-[0-9]{6}-[0-9]{2}$/.test(licenseNumber);
      if (isAlreadyFormatted) return licenseNumber; // 이미 형식에 맞게 하이픈이 있는 경우 그대로 반환

      let trimmedLicenseNumber = licenseNumber.replace(/\s/g, '');
      let removedHyphenLicenseNumber = trimmedLicenseNumber.replace(/-/g, "");
      // 하이픈을 추가하여 반환
      return removedHyphenLicenseNumber.replace(/(\d{2})(\d{2})(\d{6})(\d{2})/, '$1-$2-$3-$4');//끝에 $넣으면 자릿수 고정.. 
    }
  }

  const calculateAgeFromBirthdate = (birthdate: string): number => {
    // "yyyy-mm-dd" 형식을 받아서 나이를 계산
    const birthYear = parseInt(birthdate.substr(0, 4)); // 연도
    const birthMonth = parseInt(birthdate.substr(5, 2)) - 1; // 월
    const birthDay = parseInt(birthdate.substr(8, 2)); // 일

    const birthDate = new Date(birthYear, birthMonth, birthDay);
    const today = new Date();

    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age;
  }


  const sliceLicenseNumber = (licenseNumber: string): string => {
    const index = licenseNumber.split("-")

    return index[1]
  }

  const calculateYearsSinceIssued = (issueYearLastTwoDigits: number): number => {
    const currentYearLastTwoDigits = parseInt(String(new Date().getFullYear()).slice(-2));
    const currentCentury = Math.floor(new Date().getFullYear() / 100) * 100; // 현재 세기

    const issueCentury = issueYearLastTwoDigits < currentYearLastTwoDigits ? currentCentury : currentCentury - 100; // 발급 연도의 세기
    const issueYear = issueCentury + issueYearLastTwoDigits; // 발급 연도 완성
    const currentYear = currentCentury + currentYearLastTwoDigits; // 현재 연도 완성

    return currentYear - issueYear;
  };

  const licneseAlertMsg = (value: string) => {
    if (value.trim().includes("운전자명")) {
      return '<span style="font-weight:500; font-size : 1.3em">운전자명이 잘못 인식됩니다.</span> <br/> <br/> 입력된 운전자 정보를 확인 하시고 <br/> 수정 버튼을 눌러 직접 입력해주신 후 <br/> <span style="color:#3e83f1">"인증"</span> 버튼 눌러주세요.<br/> 혹은 재촬영 버튼을 누른 후 <br/> 재스캔을 해주세요.</span>'
    } else if (value.trim().includes("번호")) {
      return '<span style="font-weight:500; font-size : 1.3em">면허번호가 잘못 인식됩니다. <br/> 입력된 운전자 정보를 확인 하시고 <br/> 수정 버튼을 눌러 직접 입력해주신 후 <br/> <span style="color:#3e83f1">"인증"</span> 버튼 눌러주세요.<br/> 혹은 재촬영 버튼을 누른 후 <br/> 재스캔을 해주세요.</span>'
    } else {
      return '<span style="font-weight:500; font-size : 1.3em">면허 정보 인식에 실패했습니다. <br/> 입력된 운전자 정보를 확인 하시고 <br/> 수정 버튼을 눌러 직접 입력해주신 후 <br/> <span style="color:#3e83f1">"인증"</span> 버튼 눌러주세요.<br/> 혹은 재촬영 버튼을 누른 후 <br/> 재스캔을 해주세요.</span>'
    }
  }

  // font-weight: 700;
  // font-size: 1.3em;

  const unicodeReplaceAll = (text: string): string => {
    return text.replace(/\\u[\dA-F]{4}/gi, match => {
      return String.fromCharCode(parseInt(match.replace(/\\u/g, ''), 16));
    });
  };

  const formatBirthTime = (time: number): string => {
    const date = new Date(time);
    const year = date.getFullYear().toString().slice(-2); // 연도의 끝 두 자리
    const month = (date.getMonth() + 1).toString().padStart(2, '0'); // 월
    const day = date.getDate().toString().padStart(2, '0'); // 일

    return `${year}${month}${day}`;
  };

  const formatBirthDaySummary = (birthday: string): string => {
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    if (!regex.test(birthday)) {
      return ""; // 형식이 잘못되면 null 반환
    }

    const [year, month, day] = birthday.split('-');
    const yy = year.slice(2); // 뒤 2자리만 사용
    return `${yy}${month}${day}`;
  }

  const parsingLicenseIssuedYear = (licenseNum: string): string => {
    const addedHyphenLicenseNum = addHyphenToLicenseNumber(licenseNum);
    const splitedLicenseNum = addedHyphenLicenseNum.split("-");

    if(splitedLicenseNum.length !== 4) { // 제대로된 면허증 규격이 아닌 경우 빈 문자열 return
      return "";
    }

    return splitedLicenseNum[1];
  }

  return {
    formatPhoneNumber,
    formatLicenseName,
    formatLicenseType,
    formatNumber,
    formatLicenseNameText,
    formatDate,
    formatDate2,
    compareWithCurrentTime,
    calculateDrivingExperience,
    calculateAge,
    isValidDate,
    isValidDate2,
    formatLicenseType2,
    getNextDayIfPast,
    addHyphenToLicenseNumber,
    calculateAgeFromBirthdate,
    sliceLicenseNumber,
    calculateYearsSinceIssued,
    unicodeReplaceAll,
    licneseAlertMsg,
    formatBirthTime,
    formatBirthDaySummary,
    parsingLicenseIssuedYear
  };
};
